<template>
  <div>
    <div class="d-flex flex-column mb-2">
      <strong>{{ $t("new-event.sector-map.has-sector") }}</strong>
      <small v-if="getEventSectorData.hasSector"
        >{{ $t("new-event.sector-map.subtitle") }}
      </small>
    </div>
    <v-radio-group
      class="my-2"
      height="1"
      row
      v-model="getEventSectorData.hasSector"
    >
      <v-radio
        :color="$config.dados.primary"
        class="mr-5"
        :label="$t('new-event.sector-map.no')"
        :value="false"
      />
      <v-radio
        :color="$config.dados.primary"
        :label="$t('new-event.sector-map.yes')"
        :value="true"
      />
    </v-radio-group>
    <v-form
      v-if="getEventSectorData.hasSector"
      @submit.prevent
      ref="form"
      v-model="valid"
      lazy-validation
    >
      <v-row no-gutters>
        <v-col cols="12"
          ><strong>{{ $t("new-event.sector-map.add-sector") }}</strong></v-col
        >

        <v-col class="bg-background rounded-lg py-3">
          <v-row no-gutters>
            <v-col cols="12" sm="6">
              <BaseTextField
                :placeholder="$t('new-event.sector-map.placeholder.name')"
                required
                class="mb-4"
                :label="`${$t('new-event.sector-map.input.name')} ${
                  selectedLocales?.length > 0
                    ? `(${getTextLocale(locale)})`
                    : ''
                }`"
                v-model="sectorDefault"
                :rules="[rules.required]"
                @input="validateForm"
                maxlength="50"
              />
              <div v-for="loc in selectedLocales" :key="loc">
                <BaseTextField
                  required
                  class="mb-4"
                  :placeholder="$t('new-event.sector-map.placeholder.name')"
                  :label="`${$t(
                    'new-event.sector-map.input.name'
                  )} (${getTextLocale(loc)})`"
                  :rules="[rules.required]"
                  v-model="sectorLocales[loc]"
                  @input="validateForm"
                  maxlength="50"
                />
              </div>
            </v-col>
            <v-col cols="12" sm="1" class="d-flex justify-center align-start">
              <v-btn
                :disabled="!valid"
                :color="$config.dados.primary"
                class="rounded-pill white--text"
                @click="addSector"
              >
                <v-icon dark>
                  {{ selectedSectorIndex !== null ? "mdi-pencil" : "mdi-plus" }}
                </v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" sm="5" class="rounded-lg align-start pt-1 d-flex">
              <div class="chip-container">
                <div
                  v-for="(sector, index) in formattedSectors"
                  :key="index"
                  class="chip-wrapper"
                >
                  <v-tooltip bottom v-if="sector.translations">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        class="mb-1 ml-1"
                        :key="index"
                        v-bind="attrs"
                        v-on="on"
                        appen
                      >
                        <template v-slot:default>
                          <div
                            class="d-flex justify-space-between align-center"
                          >
                            <span class="mr-2">{{ sector.descricao }}</span>
                            <v-icon
                              small
                              class="mr-1"
                              @click="selectSector(sector, index)"
                            >
                              mdi-pencil
                            </v-icon>
                            <v-icon
                              v-if="removingLoading !== index"
                              small
                              :disabled="selectedSectorIndex !== null"
                              @click="removeSector(index)"
                            >
                              mdi-delete
                            </v-icon>
                            <v-progress-circular
                              v-else
                              :width="2"
                              size="20"
                              indeterminate
                            />
                          </div>
                        </template>
                      </v-chip>
                    </template>
                    <span>{{ sector.translations }}</span>
                  </v-tooltip>
                  <v-chip v-else class="mb-1 ml-1" :key="index">
                    <template v-slot:default>
                      <div class="d-flex justify-space-between align-center">
                        <span class="mr-2">{{ sector.descricao }}</span>
                        <v-icon
                          small
                          class="mr-1"
                          @click="selectSector(sector, index)"
                        >
                          mdi-pencil
                        </v-icon>
                        <v-icon
                          v-if="removingLoading !== index"
                          small
                          :disabled="selectedSectorIndex !== null"
                          @click="removeSector(index)"
                          >mdi-delete</v-icon
                        >
                        <v-progress-circular
                          v-else
                          :width="2"
                          size="20"
                          indeterminate
                        />
                      </div>
                    </template>
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { getTextLocale } from "@/utils";
import BaseTextField from "../forms/BaseTextField.vue";
import { mapGetters, mapActions } from "vuex";
import mixins from "@/mixins";

const { formRules } = mixins;
export default {
  name: "SectorMap",
  components: { BaseTextField },
  data() {
    return {
      getTextLocale,
      sectors: [],
      valid: false,
      sectorDefault: "",
      sectorLocales: {},
      removingLoading: "",
      selectedSectorIndex: null,
    };
  },
  mixins: [formRules],
  computed: {
    ...mapGetters([
      "getEventSectorData",
      "getSelectedLocales",
      "getTicketList",
    ]),
    selectedLocales() {
      return this.getSelectedLocales;
    },
    locale() {
      return this.$i18n.locale;
    },
    formattedSectors() {
      return this.sectors.map(sector => {
        const locales = sector.locales;
        const descricao = locales[this.locale] || sector.descricao;

        const translations = Object.keys(locales)
          .filter(key => key !== this.locale)
          .map(key => `${this.getTextLocale(key)}: ${locales[key]}`)
          .join(", ");

        return {
          ...sector,
          descricao,
          translations,
        };
      });
    },

    formattedSectorDescription() {
      const defaultSector = `<${this.locale}>${this.sectorDefault}</${this.locale}>`;
      if (!this.selectedLocales?.length) {
        return defaultSector;
      }
      return (
        defaultSector +
        this.selectedLocales
          .map(
            selectedLocale =>
              `<${selectedLocale}>${this.sectorLocales[selectedLocale]}</${selectedLocale}>`
          )
          .join("")
      );
    },
    isValidForm() {
      return this.valid;
    },
  },
  methods: {
    ...mapActions([
      "deleteSector",
      "showSnackbarSuccess",
      "showSnackbarError",
      "createSector",
      "updateSector",
    ]),
    validateForm() {
      this.$refs.form.validate();
    },
    async addSector() {
      try {
        await this.validateForm();
        const existingSector = this.sectors.find(
          sector => sector.descricao === this.sectorDefault
        );
        if (existingSector && this.selectedSectorIndex === null) {
          this.showSnackbarError(
            this.$t("new-event.sector-map.duplicate-error")
          );
          return;
        }
        if (!this.isValidForm) return;

        if (this.selectedSectorIndex !== null) {
          this.editSector();
        } else {
          this.sectors.push({
            descricao: this.sectorDefault,
            locales: this.sectorLocales,
          });
          this.getEventSectorData.sectors.push(this.formattedSectorDescription);

          this.createSector([this.formattedSectorDescription]);
        }

        this.sectorDefault = "";
        this.sectorLocales = {};
        this.selectedSectorIndex = null;
        this.$nextTick(() => {
          this.showSnackbarSuccess(this.$t("new-event.sector-map.add-success"));
        });
      } catch (error) {
        console.log(error);
      }
    },
    editSector() {
      this.sectors[this.selectedSectorIndex] = {
        descricao: this.sectorDefault,
        locales: this.sectorLocales,
      };
      this.sectors = [...this.sectors];
      this.getEventSectorData.sectors[this.selectedSectorIndex] = {
        ...this.getEventSectorData.sectors[this.selectedSectorIndex],
        descricao: this.formattedSectorDescription,
      };

      this.updateSector(this.getEventSectorData.sectors);
    },
    selectSector(sector, index) {
      this.sectorDefault = sector.descricao;
      this.sectorLocales = sector.locales;
      this.selectedSectorIndex = index;
    },
    async removeSector(index, force = false) {
      const sector = this.getEventSectorData?.sectors[index];

      const hasTicketsWithSector = this.getTicketList?.some(
        ticket => ticket.sector === sector.idsetor
      );

      if (hasTicketsWithSector && !force) {
        this.showSnackbarError(
          this.$t("new-event.sector-map.remove-error-tickets-exist")
        );
        return;
      }

      if (index === this.selectedSectorIndex) this.selectedSectorIndex = null;

      this.removingLoading = index;
      if (sector && sector.idsetor) {
        try {
          if (force) {
            this.getTicketList.forEach(ticket => {
              if (ticket.sector === sector.idsetor) {
                ticket.sector = null;
              }
            });
          }
          await this.deleteSector(sector.idsetor);
          const sectorIndex = this.getEventSectorData.sectors.findIndex(
            s => s.idsetor === sector.idsetor
          );
          if (sectorIndex > -1) {
            this.sectors.splice(sectorIndex, 1);
          }
          this.getEventSectorData.sectors.splice(index, 1);
          this.showSnackbarSuccess(
            this.$t("new-event.sector-map.remove-success")
          );
        } catch (e) {
          console.error(e);
        } finally {
          this.removingLoading = null;
        }
      } else {
        this.sectors.splice(index, 1);
        this.getEventSectorData.sectors.splice(index, 1);
        this.showSnackbarSuccess(
          this.$t("new-event.sector-map.remove-success")
        );
        this.removingLoading = null;
      }
    },
    async removeAllSectors() {
      const sectorCount = this.sectors.length;
      for (let i = sectorCount - 1; i >= 0; i--) {
        await this.removeSector(i, true);
      }
    },
    transformData(input) {
      const currentLocale = this.$i18n.locale;

      return input.map(item => {
        const matches = item.descricao.match(/<([^>]+)>(.*?)<\/\1>/g);
        const locales = {};
        let descricao = "";

        matches.forEach(match => {
          const parts = match.match(/<([^>]+)>(.*?)<\/\1>/);
          const locale = parts[1];
          const text = parts[2];

          if (locale === currentLocale) {
            descricao = text;
          } else {
            locales[locale] = text;
          }
        });

        return {
          descricao: descricao,
          locales: locales,
        };
      });
    },
  },
  watch: {
    getEventSectorData() {
      if (!this.getEventSectorData.sectors) return;
      this.sectors = this.getEventSectorData.sectors
        .map(sector => {
          return {
            descricao: sector.slice(0, sector.indexOf(">")),
            locales: sector
              .slice(sector.indexOf(">") + 1, sector.lastIndexOf("<"))
              .split("<"),
          };
        })
        .reverse();
    },
    "$store.getters.getEventSectorData.sectors"() {
      if (this.getEventSectorData.sectors && !this.sectors.length) {
        this.selectedSectorIndex = null;
        this.sectors = this.transformData(this.getEventSectorData.sectors);
      }
    },
    "getEventSectorData.hasSector"(newValue) {
      if (!newValue) {
        this.removeAllSectors();
      }
    },
  },
  mounted() {
    if (this.getEventSectorData.sectors && !this.sectors.length) {
      this.sectors = this.transformData(this.getEventSectorData.sectors);
    }
  },
};
</script>

<style scoped>
.chip-container {
  display: flex;
  flex-wrap: wrap;
}
.chip-wrapper {
  margin-bottom: 4px;
}
</style>
